import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, API_BASE, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  MapContainer, TileLayer, CircleMarker, Popup, useMap, Polyline,
} from "react-leaflet";
import L from "leaflet";
import {
  MagnifyingGlass, X, DownloadSimple, ArrowsClockwise, MapPin,
  Users, UserCircle, Buildings, Heart, Clock, NavigationArrow,
} from "@phosphor-icons/react";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const KIND_META = {
  staff:    { label: "Field staff", colour: "#2D5A27", soft: "#C7E0C2", icon: Users },
  user:     { label: "Office",      colour: "#8B5A2B", soft: "#E8D7C2", icon: UserCircle },
  partner:  { label: "Partner",     colour: "#7F4EC0", soft: "#DDD1EF", icon: Buildings },
  customer: { label: "Customer",    colour: "#C04E8B", soft: "#EFCFDE", icon: Heart },
};

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points?.length) return;
    const bounds = L.latLngBounds(points.map(p => [p.lat, p.lng]));
    try { map.fitBounds(bounds.pad(0.2)); } catch { /* noop */ }
  }, [points, map]);
  return null;
}

export default function FindGpsDashboard() {
  const [data, setData] = useState(null);
  const [role, setRole] = useState("all");
  const [online, setOnline] = useState("all");
  const [q, setQ] = useState("");
  const [tick, setTick] = useState(0);
  const [selected, setSelected] = useState(null);
  const [historyDate, setHistoryDate] = useState("");
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // 30-second refresh. Also refreshes on filter changes.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/findgps/overview", {
          params: { role, online, q: q || undefined },
        });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      }
    };
    load();
    const int = setInterval(() => setTick(t => t + 1), 30000);
    return () => { cancelled = true; clearInterval(int); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, online, q, tick]);

  const entities = data?.entities || [];
  const counts = data?.counts || {};

  const mapPoints = useMemo(
    () => entities.map(e => ({ lat: e.lat, lng: e.lng, e })),
    [entities]
  );

  const openHistory = async (ent, dateStr) => {
    const d = dateStr || new Date().toISOString().slice(0, 10);
    setHistoryDate(d);
    setHistoryLoading(true);
    try {
      const r = await api.get(
        `/findgps/entity/${ent.kind}/${ent.id}/history`,
        { params: { date: d } }
      );
      setHistoryData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
      setHistoryData(null);
    }
    setHistoryLoading(false);
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("rescigo_token");
      const url = new URL(`${API_BASE}/findgps/export.csv`);
      if (role && role !== "all") url.searchParams.set("role", role);
      if (online && online !== "all") url.searchParams.set("online", online);
      if (q) url.searchParams.set("q", q);
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `findgps-${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const RoleChip = ({ k, label }) => {
    const active = role === k;
    const n = k === "all" ? counts.total : counts[k];
    return (
      <button
        type="button"
        data-testid={`findgps-chip-${k}`}
        onClick={() => setRole(k)}
        className={[
          "h-9 px-3 rounded-full text-xs font-medium border transition",
          active
            ? "bg-[#2D5A27] text-white border-[#2D5A27]"
            : "bg-white text-[#2F2D29] border-[#D9D5C6] hover:border-[#2D5A27]",
        ].join(" ")}
      >
        {label} · {n ?? 0}
      </button>
    );
  };

  const OnlineToggle = ({ v, label }) => {
    const active = online === v;
    return (
      <button
        type="button"
        data-testid={`findgps-online-${v}`}
        onClick={() => setOnline(v)}
        className={[
          "h-9 px-3 rounded-full text-xs font-medium border transition",
          active
            ? "bg-[#2F2D29] text-white border-[#2F2D29]"
            : "bg-white text-[#2F2D29] border-[#D9D5C6] hover:border-[#2F2D29]",
        ].join(" ")}
      >
        {label}
      </button>
    );
  };

  return (
    <AppShell>
      <div className="min-h-screen bg-[#F7F6F2]" data-testid="findgps-page">
        {/* ---------- Header ---------- */}
        <div className="px-6 pt-6 pb-3 border-b border-[#E5E2D9]">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#8A8780]">
                Mission Control
              </div>
              <h1 className="text-3xl font-heading mt-1 flex items-center gap-3">
                <NavigationArrow size={28} weight="duotone" className="text-[#2D5A27]" />
                FindGPS · Live Tracker
              </h1>
              <p className="text-sm text-[#5C5A55] mt-1">
                Every logged-in staff, partner, and in-flight customer on one map.
                Refreshes every 30s. Online = pinged in the last 2 min.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button" onClick={() => setTick(t => t + 1)}
                data-testid="findgps-refresh"
                className="h-9 px-3 rounded-md border border-[#D9D5C6] bg-white text-xs flex items-center gap-2"
              >
                <ArrowsClockwise size={16} /> Refresh
              </button>
              <button
                type="button" onClick={exportCsv}
                data-testid="findgps-export"
                className="h-9 px-3 rounded-md bg-[#2D5A27] text-white text-xs flex items-center gap-2 hover:bg-[#3A7033]"
              >
                <DownloadSimple size={16} /> Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* ---------- Filters ---------- */}
        <div className="px-6 py-3 border-b border-[#E5E2D9] bg-white/60 flex items-center gap-2 flex-wrap">
          <RoleChip k="all" label="All" />
          <RoleChip k="staff" label="Staff" />
          <RoleChip k="user" label="Office" />
          <RoleChip k="partner" label="Partners" />
          <RoleChip k="customer" label="Customers" />
          <span className="w-px h-6 bg-[#D9D5C6] mx-2" />
          <OnlineToggle v="all" label="All" />
          <OnlineToggle v="online" label={`Online · ${counts.online ?? 0}`} />
          <OnlineToggle v="offline" label={`Offline · ${counts.offline ?? 0}`} />
          <div className="ml-auto flex items-center gap-2 bg-white rounded-md border border-[#D9D5C6] px-2 h-9">
            <MagnifyingGlass size={14} className="text-[#8A8780]" />
            <input
              value={q} onChange={e => setQ(e.target.value)}
              placeholder="name · phone · ID · SR"
              data-testid="findgps-search"
              className="h-full text-xs outline-none bg-transparent w-48"
            />
            {q && (
              <button onClick={() => setQ("")} data-testid="findgps-search-clear">
                <X size={14} className="text-[#8A8780]" />
              </button>
            )}
          </div>
        </div>

        {/* ---------- Legend ---------- */}
        <div className="px-6 py-2 flex items-center gap-4 flex-wrap text-[11px] text-[#5C5A55]">
          {Object.entries(KIND_META).map(([k, m]) => (
            <span key={k} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full"
                     style={{ background: m.colour }} />
              {m.label}
            </span>
          ))}
        </div>

        {/* ---------- Main (list + map) ---------- */}
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-0 min-h-[70vh]">
          {/* List */}
          <aside className="border-r border-[#E5E2D9] bg-white max-h-[75vh] overflow-y-auto"
                 data-testid="findgps-list">
            {entities.length === 0 ? (
              <div className="p-6 text-sm text-[#8A8780]">No one matches your filter yet.</div>
            ) : entities.map(e => {
              const meta = KIND_META[e.kind] || KIND_META.staff;
              const Ico = meta.icon;
              return (
                <button
                  type="button"
                  key={`${e.kind}:${e.id}`}
                  data-testid={`findgps-row-${e.kind}-${e.id}`}
                  onClick={() => setSelected(e)}
                  className={[
                    "w-full text-left px-4 py-3 border-b border-[#F0EEE4] hover:bg-[#F7F6F2] flex items-center gap-3",
                    selected?.id === e.id && selected?.kind === e.kind
                      ? "bg-[#F0EEE4]" : "",
                  ].join(" ")}
                >
                  <span
                    className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: e.online ? meta.colour : meta.soft }}
                  >
                    <Ico size={16} weight="bold"
                           color={e.online ? "white" : meta.colour} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-[#2F2D29] truncate">
                      {e.name}
                    </div>
                    <div className="text-[11px] text-[#8A8780] truncate">
                      {meta.label} · {e.role}
                      {e.phone ? ` · ${e.phone}` : ""}
                    </div>
                  </div>
                  <span className={[
                    "text-[10px] uppercase tracking-wider",
                    e.online ? "text-[#2D5A27]" : "text-[#B45B3F]",
                  ].join(" ")}>
                    {e.online ? "online" : "offline"}
                  </span>
                </button>
              );
            })}
          </aside>

          {/* Map */}
          <div className="relative min-h-[70vh]">
            <MapContainer
              center={[9.9312, 76.2673]} zoom={11} className="w-full h-full"
              style={{ minHeight: "70vh" }} data-testid="findgps-map"
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; OpenStreetMap'
              />
              <FitBounds points={mapPoints} />
              {entities.map(e => {
                const meta = KIND_META[e.kind] || KIND_META.staff;
                return (
                  <CircleMarker
                    key={`${e.kind}:${e.id}`}
                    center={[e.lat, e.lng]}
                    radius={e.online ? 10 : 7}
                    pathOptions={{
                      color: meta.colour,
                      weight: 2,
                      fillColor: meta.colour,
                      fillOpacity: e.online ? 0.75 : 0.3,
                    }}
                    eventHandlers={{ click: () => setSelected(e) }}
                  >
                    <Popup>
                      <div className="text-xs">
                        <div className="font-semibold">{e.name}</div>
                        <div>{meta.label} · {e.role}</div>
                        {e.phone && <div>{e.phone}</div>}
                        <div className="opacity-70 mt-1">
                          {e.online ? "Online now" : (e.last_ts || "")}
                        </div>
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          </div>
        </div>

        {/* ---------- Drawer ---------- */}
        {selected && (
          <div className="fixed inset-0 z-40 bg-black/40 flex justify-end"
                data-testid="findgps-drawer">
            <div className="w-full sm:max-w-md bg-white h-full overflow-y-auto shadow-2xl">
              <div className="p-5 border-b border-[#E5E2D9] flex items-start justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-[#8A8780]">
                    {KIND_META[selected.kind]?.label}
                  </div>
                  <h3 className="text-xl font-heading mt-1">{selected.name}</h3>
                  <div className="text-xs text-[#5C5A55] mt-1">
                    {selected.role}{selected.phone ? ` · ${selected.phone}` : ""}
                  </div>
                  <div className="text-[11px] text-[#8A8780] mt-1">
                    Last ping: {selected.last_ts || "n/a"} · {selected.online ? "online" : "offline"}
                  </div>
                </div>
                <button onClick={() => { setSelected(null); setHistoryData(null); }}
                         data-testid="findgps-drawer-close"
                         className="p-1 rounded hover:bg-[#F0EEE4]">
                  <X size={18} />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={historyDate || new Date().toISOString().slice(0,10)}
                    onChange={e => setHistoryDate(e.target.value)}
                    data-testid="findgps-history-date"
                    className="h-9 px-2 rounded-md border border-[#D9D5C6] text-xs"
                  />
                  <button
                    type="button"
                    onClick={() => openHistory(selected, historyDate)}
                    data-testid="findgps-load-history"
                    className="h-9 px-3 rounded-md bg-[#2D5A27] text-white text-xs flex items-center gap-1"
                  >
                    <Clock size={14} /> Load history
                  </button>
                </div>
                {historyLoading && (
                  <div className="text-xs text-[#8A8780]">Loading trail…</div>
                )}
                {historyData && !historyLoading && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="p-2 rounded bg-[#F7F6F2]">
                        <div className="text-[10px] text-[#8A8780] uppercase">Pings</div>
                        <div className="text-sm font-semibold">{historyData.point_count}</div>
                      </div>
                      <div className="p-2 rounded bg-[#F7F6F2]">
                        <div className="text-[10px] text-[#8A8780] uppercase">Distance</div>
                        <div className="text-sm font-semibold">
                          {((historyData.total_distance_m || 0) / 1000).toFixed(2)} km
                        </div>
                      </div>
                      <div className="p-2 rounded bg-[#F7F6F2]">
                        <div className="text-[10px] text-[#8A8780] uppercase">Stops</div>
                        <div className="text-sm font-semibold">{historyData.stops?.length || 0}</div>
                      </div>
                    </div>
                    <div className="h-56 rounded overflow-hidden border border-[#E5E2D9]">
                      <MapContainer
                        center={[
                          historyData.segments?.[0]?.a_lat ?? selected.lat,
                          historyData.segments?.[0]?.a_lng ?? selected.lng,
                        ]}
                        zoom={13}
                        className="w-full h-full"
                        data-testid="findgps-history-map"
                      >
                        <TileLayer
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {historyData.segments?.length > 0 && (
                          <Polyline
                            positions={[
                              [historyData.segments[0].a_lat, historyData.segments[0].a_lng],
                              ...historyData.segments.map(s => [s.b_lat, s.b_lng]),
                            ]}
                            pathOptions={{ color: "#2D5A27", weight: 3 }}
                          />
                        )}
                        {historyData.stops?.map((s, i) => (
                          <CircleMarker
                            key={i}
                            center={[s.lat, s.lng]}
                            radius={6}
                            pathOptions={{ color: "#B45B3F", fillColor: "#F9E4B7", fillOpacity: 0.9 }}
                          />
                        ))}
                      </MapContainer>
                    </div>
                    <div className="text-[11px] text-[#8A8780]">
                      Moving {(historyData.moving_time_s/60).toFixed(0)}m · idle {(historyData.idle_time_s/60).toFixed(0)}m
                    </div>
                  </div>
                )}
                {selected.phone && (
                  <div className="pt-3 border-t border-[#E5E2D9] flex gap-2">
                    <a href={`tel:${selected.phone}`}
                        data-testid="findgps-call"
                        className="flex-1 h-9 rounded-md bg-[#F0EEE4] text-xs flex items-center justify-center">
                      Call
                    </a>
                    <a href={`https://wa.me/${(selected.phone || "").replace(/\D/g,"")}`}
                        target="_blank" rel="noreferrer"
                        data-testid="findgps-wa"
                        className="flex-1 h-9 rounded-md bg-[#2D5A27] text-white text-xs flex items-center justify-center">
                      WhatsApp
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
